import { useDecision } from '@optimizely/react-sdk'
import { useTranslation } from 'next-i18next'
import { type FC, Fragment, useContext, useState } from 'react'

import { ToggleFilter as DeprecatedToggleFilter } from '@/components/shared/deprecated/toggle-filter/ToggleFilter'
import { FILTER_CANONICAL } from '@/constants/search'
import { type SearchContextClient, SearchContext } from '@/contexts/SearchContext'
import { getLocaleLabelKeyOverride } from '@/domain/filters'
import { getExpandedFilterIdList } from '@/domain/srp/filters/getExpandedFilterIdList'
import { getParentFilter } from '@/domain/srp/filters/getParentFilter'
import {
  type AppliedAttributeFilter,
  type AppliedRangeFilter,
  type AttributeFilter,
  type DateFilter as DateFilterType,
  type FilterGroup,
  type GetSearchResultsPageByUrlQuery,
  type RangeFilter,
  FilterControlType,
} from '@/generated'
import { useGetSearchResultsData } from '@/hooks/srp/useGetSearchResultsData'
import { type SearchActionsResult, useSearchActions } from '@/hooks/srp/useSearchActions'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { FEATURE_FLAG } from '@/lib/optimizely'
import {
  type RangeFilterMinMax,
  isAttributeFilter,
  isCategoryTree,
  isDateFilter,
  isLocationTree,
  isRangeFilterWithMinMax,
} from '@/types/search'
import { Accordion } from '@/ui/atoms/accordion'
import { isAnyJobsCategory, isAnyVacationRentalsCategory } from '@/utils/category/categoryTree'

import { CategoryTreeFilter } from '../filter-types/CategoryTreeFilter'
import { DateFilter } from '../filter-types/DateFilter'
import { DateRangeFilter } from '../filter-types/DateRangeFilter'
import { IntegerRangeFilter } from '../filter-types/IntegerRangeFilter'
import { LocationTreeFilter } from '../filter-types/LocationTreeFilter'
import { MultiSelectFilter } from '../filter-types/MultiSelectFilter'
import { RadioFilter } from '../filter-types/RadioFilter'
import { ToggleFilter } from '../filter-types/ToggleFilter'
import { FiltersDivider } from '../styled'

type CommonFilterProps = {
  isMobile?: boolean
  /** Label of the filter accordion */
  label: string
  /** Canonical name of the filter */
  name: string
  /** Parent filter information */
  parentFilter?: { isSelected: boolean; label: string }
  /** Function to refetch results on filter selection */
  refetchResults: SearchActionsResult['refetchResults']
  /** Available SRP data */
  data?: GetSearchResultsPageByUrlQuery['searchResultsPageByUrl']
}

export type AttributeFilterProps = CommonFilterProps & {
  /** Applied attribute filter list */
  appliedFilters: AppliedAttributeFilter[]
  /** Current attribute filter to be rendered */
  attributeFilter: AttributeFilter
}

export type RangeFilterProps = CommonFilterProps & {
  /** Applied range filter list */
  appliedFilters: AppliedRangeFilter[]
  /** Current range filter to be rendered */
  rangeFilter: RangeFilterMinMax
}

export type FilterComponentProps = {
  label: FilterGroup['label']
  name: FilterGroup['name']
  parentFilter?: { isSelected: boolean; label: string }
  onDateFilterChange: SearchContextClient['handleDateFiltersChange']
  resetDateFilter: SearchContextClient['resetDateFilter']
  onDateRangeFilterChange: SearchContextClient['handleDateRangeFiltersChange']
  rangeFilter?: RangeFilter
  dateFilter?: DateFilterType
  isSrpLoading?: boolean
}

type filterComponentsProps = {
  [FilterControlType.DateRange]: FC<FilterComponentProps>
  [FilterControlType.Date]: FC<FilterComponentProps>
}

const filterComponents: filterComponentsProps = {
  DATE: DateFilter,
  DATE_RANGE: DateRangeFilter,
}

export type FiltersAccordionProps = {
  isMobile?: boolean
}

export const FiltersAccordion: FC<FiltersAccordionProps> = ({ isMobile }) => {
  const { data, setLoadingStates } = useGetSearchResultsData()
  const { controls, searchQuery } = data || {}
  const { refetchResults, attributeAppliedFilters, rangeAppliedFilters } = useSearchActions(
    searchQuery?.filters
  )

  const filters = controls?.filtering
  const selectedCategoryId = searchQuery?.category?.id || 0

  const { resetDateFilter, handleDateRangeFiltersChange, handleDateFiltersChange, loadingResults } =
    useContext(SearchContext)

  const { t } = useTranslation(['srp'])
  const [{ enabled: shouldShowNewToggleFilters }] = useDecision(
    FEATURE_FLAG.SUPPORT_NEW_SCHEMA_FOR_TOGGLES_FILTER
  )
  const ToggleFilterComponent = shouldShowNewToggleFilters ? ToggleFilter : DeprecatedToggleFilter

  const preExpandedFilters = isMobile ? [] : getExpandedFilterIdList(selectedCategoryId)
  const [expandedFilters, setExpandedFilters] = useState(preExpandedFilters)

  const getComponentFromType = (controlType: string) => {
    switch (controlType) {
      case FilterControlType.Date:
        return filterComponents['DATE']
      case FilterControlType.DateRange:
        return filterComponents['DATE_RANGE']
      default:
        return null
    }
  }
  /**
   * callback that fires when an accordion item is clicked
   * i.e. when a user opens/closes a filter
   * @param uuidArray contains a list of uuids for all expanded filters
   */
  const handleAccordionItemClick = (uuidArray: string[]) => {
    // only fire tracking event when the filter opens, not when it closes
    if (uuidArray.length > expandedFilters.length) {
      trackEvent({ action: GA_EVENT.FilterSelectBegin })
    }

    setExpandedFilters(uuidArray)
  }

  return (
    <>
      <Accordion gap={0} preExpanded={preExpandedFilters} onChange={handleAccordionItemClick}>
        {!filters?.length ? null : (
          <CategoryTreeFilter
            data={data}
            filters={filters}
            refetchResults={refetchResults}
            setLoadingStates={setLoadingStates}
          />
        )}

        <FiltersDivider />

        {!filters?.length || isAnyVacationRentalsCategory(selectedCategoryId) ? null : (
          <LocationTreeFilter refetchResults={refetchResults} data={data} filters={filters} />
        )}

        {filters?.map((item, index) => {
          return item.filters.map((filter) => {
            if (isCategoryTree(filter) || isLocationTree(filter)) return null

            // Do not show Salary filter in any of the Jobs categories
            if (
              isAnyJobsCategory(selectedCategoryId) &&
              filter.name === FILTER_CANONICAL.PRICE_RANGE
            ) {
              return null
            }

            const attributeFilter = isAttributeFilter(filter) ? filter : undefined

            let label = filter.label
            const localeKeyOverride = getLocaleLabelKeyOverride(filter.name, selectedCategoryId)
            if (localeKeyOverride) {
              label = t(`filters.${localeKeyOverride}_range.${localeKeyOverride}`)
            }

            const parentFilter = getParentFilter(filter, filters)
            const key = `${filter.name}-${index}`

            if (attributeFilter) {
              const attributeProps: AttributeFilterProps = {
                appliedFilters: attributeAppliedFilters,
                attributeFilter,
                data,
                isMobile,
                label,
                name: filter.name,
                parentFilter,
                refetchResults,
              }

              return (
                <Fragment key={key}>
                  <FiltersDivider />
                  {filter.type === FilterControlType.Checkboxes ||
                  filter.type === FilterControlType.MultiSelect ? (
                    <MultiSelectFilter {...attributeProps} />
                  ) : filter.type === FilterControlType.Radio ? (
                    <RadioFilter {...attributeProps} />
                  ) : filter.type === FilterControlType.Toggles ? (
                    <ToggleFilterComponent {...attributeProps} />
                  ) : null}
                </Fragment>
              )
            }

            const rangeFilter = isRangeFilterWithMinMax(filter) ? filter : undefined

            if (rangeFilter) {
              if (filter.type === FilterControlType.Range) {
                return (
                  <Fragment key={key}>
                    <FiltersDivider />

                    <IntegerRangeFilter
                      appliedFilters={rangeAppliedFilters}
                      data={data}
                      label={label}
                      name={filter.name}
                      parentFilter={parentFilter}
                      rangeFilter={rangeFilter}
                      refetchResults={refetchResults}
                    />
                  </Fragment>
                )
              }
            }

            const dateFilter = isDateFilter(filter) ? filter : undefined
            const FilterComponent = getComponentFromType(filter.type)

            return (
              <Fragment key={key}>
                <FiltersDivider />

                {FilterComponent && (
                  <FilterComponent
                    rangeFilter={rangeFilter}
                    label={label}
                    name={filter.name}
                    parentFilter={parentFilter}
                    onDateRangeFilterChange={handleDateRangeFiltersChange}
                    resetDateFilter={resetDateFilter}
                    dateFilter={dateFilter}
                    onDateFilterChange={handleDateFiltersChange}
                    isSrpLoading={loadingResults}
                  />
                )}
              </Fragment>
            )
          })
        })}
      </Accordion>
    </>
  )
}
